import type { ReactNode } from "react";

import { ApiError } from "@/api/client";
import { ErrorBox } from "@/components/Spinner";
import { Notice } from "@/components/ui";

/**
 * Why a catalog delete failed, for the confirmation that asked for it.
 *
 * A 409 is the guard doing its job: the item is still used by something
 * (tenants that allow it, gateway types that ship it) and deleting it would leave
 * those references dangling — the rtl-group / virtual-16 incident. That is not
 * an error to report, it is a refusal to explain: WHICH things block it, and
 * what to do first. Anything else is an ordinary failure (`ErrorBox`).
 *
 * The 409's body carries the blockers under `details`; `blockers` reads their
 * names out of it (the shape differs from one catalog to the other), and an empty
 * list still says "one or more <noun>" rather than nothing.
 */
export function DeleteError({
  error,
  subject,
  noun,
  blockers,
  remedy,
}: {
  error: unknown;
  /** What was being deleted, as the sentence's subject: "This entry". */
  subject: string;
  /** What blocks it, plural: "tenants". */
  noun: string;
  /** The blockers' names, read from the 409's `details`. */
  blockers: (details: Record<string, unknown> | undefined) => string[];
  /** What to do before trying again. */
  remedy: ReactNode;
}) {
  if (!(error instanceof ApiError && error.status === 409)) return <ErrorBox error={error} />;
  const details = (error.body as { details?: Record<string, unknown> } | null)?.details;
  const names = blockers(details);
  return (
    <Notice tone="warning" role="alert" title="Delete refused.">
      <p className="mt-1">
        {subject} is still used by{" "}
        {names.length > 0 ? (
          <>
            the following {noun}: <span className="font-mono">{names.join(", ")}</span>
          </>
        ) : (
          `one or more ${noun}`
        )}
        . {remedy}
      </p>
    </Notice>
  );
}
